import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen, Heart, Brain, Star, RefreshCw, RotateCcw, Search, ChevronDown, ChevronRight, LayoutGrid, List as ListIcon, Target, Play, ChevronLeft, Menu, X } from 'lucide-react';
import OpeningsTheory from '../modules/openings/OpeningsTheory';
import OpeningTraining from '../modules/training/OpeningTraining';
import OpeningExercises from '../modules/openings/OpeningExercises';
import ChessBoard from '../components/ChessBoard';
import api from '../api/client';
import { getOpeningData } from '@chess-platform/shared';
import { useOpenings } from '../hooks/useOpenings';
import { useSettings } from '../context/SettingsContext';

interface OpeningBasic {
    eco: string;
    name: string;
    fenRoot?: string;
}

type Mode = 'theory' | 'training' | 'exercises';
type Color = 'w' | 'b';

interface SessionConfig {
    eco: string;
    name: string;
    color: Color;
    mode: Mode;
}

// ── Sidebar Category Accordion ──────────────────────────────────────────────
const AccordionSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    count: number;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}> = ({ title, icon, count, isOpen, onToggle, children }) => (
    <div className="mb-2">
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl lg:rounded-2xl transition-all border border-white/5 group"
        >
            <div className="flex items-center gap-3">
                <span className="text-white/40 group-hover:text-white transition-colors">{icon}</span>
                <span className="text-[10px] lg:text-[11px] font-black text-white uppercase tracking-widest">{title}</span>
                <span className="text-[10px] font-black text-white/20 bg-black/40 px-2 py-0.5 rounded-full">{count}</span>
            </div>
            {isOpen ? <ChevronDown size={14} className="text-white/20" /> : <ChevronRight size={14} className="text-white/20" />}
        </button>
        {isOpen && (
            <div className="mt-2 flex flex-col gap-1 px-1 animate-in slide-in-from-top-1 duration-200">
                {children}
            </div>
        )}
    </div>
);

// ── Main Page ────────────────────────────────────────────────────────────────
const OpeningsPage: React.FC = () => {
    const { coreOpenings, allOpenings, allOpeningsFlat, isLoaded } = useOpenings() as any;
    const [repertoire, setRepertoire] = useState<any[]>([]);
    const [progress, setProgress] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);

    const [selectedOpening, setSelectedOpening] = useState<OpeningBasic | null>(null);
    const [sessionConfig, setSessionConfig] = useState<SessionConfig | null>(null);

    // Accordion state (exclusive)
    const [openSection, setOpenSection] = useState<'favorites' | 'explore' | null>('explore');
    const [playColor, setPlayColor] = useState<Color>('w');

    const { settings } = useSettings();
    const language = settings.language || 'es';

    // Helper to translate basic English terms to Spanish for the UI display
    // Updated with a much larger dictionary to handle the "large" database
    const translateText = (text: any, targetLang: string) => {
        const str = String(text || '');
        if (!str || str === '[object Object]') return 'Apertura';
        if (targetLang === 'en') return str; // No translation if English selected
        
        // Dictionary for common chess opening terms
        const dictionary: Record<string, string> = {
            'Defense': 'Defensa',
            'Variation': 'Variante',
            'Variation,': 'Variante,',
            'Gambit': 'Gambito',
            'Accepted': 'Aceptado',
            'Declined': 'Rehusado',
            'Opening': 'Apertura',
            'Attack': 'Ataque',
            'Counterattack': 'Contraataque',
            'System': 'Sistema',
            'Game': 'Apertura',
            'Main Line': 'Línea Principal',
            'Exchange': 'del Cambio',
            'Advance': 'del Avance',
            'Advanced': 'del Avance',
            'Classical': 'Clásica',
            'Modern': 'Moderna',
            'Old': 'Antigua',
            'Delayed': 'Retardada',
            'Closed': 'Cerrada',
            'Open': 'Abierta',
            'Semi-Closed': 'Semi-Cerrada',
            'Semi-Open': 'Semi-Abierta',
            'King\'s': 'de Rey',
            'Queen\'s': 'de Dama',
            'Knight': 'Caballo',
            'Knights': 'Caballos',
            'Bishop': 'Alfil',
            'Two': 'Dos',
            'Three': 'Tres',
            'Four': 'Cuatro',
            'Sicilian': 'Siciliana',
            'French': 'Francesa',
            'Spanish': 'Española',
            'Italian': 'Italiana',
            'Scandinavian': 'Escandinava',
            'English': 'Inglesa',
            'Russian': 'Rusa',
            'Petrov': 'Petrov',
            'Scotch': 'Escocesa',
            'Dutch': 'Holandesa',
            'Slav': 'Eslava',
            'Reti': 'Reti',
            'Catalan': 'Catalana',
            'London': 'Londres',
            'Grünfeld': 'Grünfeld',
            'Nimzo-Indian': 'Nimzo-India',
            'White': 'Blancas',
            'Black': 'Negras'
        };

        let result = str;
        
        // Match multi-word patterns specifically
        const complex = [
            { en: /King's Indian Defense/gi, es: 'Defensa India de Rey' },
            { en: /Queen's Indian Defense/gi, es: 'Defensa India de Dama' },
            { en: /King's Gambit/gi, es: 'Gambito de Rey' },
            { en: /Queen's Gambit/gi, es: 'Gambito de Dama' },
            { en: /King's Pawn/gi, es: 'Peón de Rey' },
            { en: /Queen's Pawn/gi, es: 'Peón de Dama' },
            { en: /Sicilian Defense/gi, es: 'Defensa Siciliana' },
            { en: /Caro-Kann Defense/gi, es: 'Defensa Caro-Kann' },
            { en: /London System/gi, es: 'Sistema Londres' }
        ];

        complex.forEach(p => { result = result.replace(p.en, p.es); });

        Object.entries(dictionary).forEach(([en, es]) => {
            const regex = new RegExp(`\\b${en}\\b`, 'gi');
            result = result.replace(regex, es);
        });

        return result
            .replace(/India de Rey Defensa/gi, 'Defensa India de Rey')
            .replace(/India de Dama Defensa/gi, 'Defensa India de Dama')
            .replace(/Apertura de Rey/gi, 'Apertura de Peón de Rey')
            .replace(/Apertura de Dama/gi, 'Apertura de Peón de Dama')
            .replace(/variante del Cambio/gi, 'variante del cambio')
            .replace(/variante del Avance/gi, 'variante del avance');
    };

    const normalize = (val: string) => 
        String(val || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // --- MOVE HOOKS TO TOP (Rule of Hooks) ---
    const filteredOpenings = useMemo(() => {
        const queryNorm = normalize(search);
        if (!queryNorm) return [];
        
        const terms = queryNorm.split(' ').filter(w => w.length > 0);
        const source = Array.isArray(allOpeningsFlat) ? allOpeningsFlat : [];
        
        const results = source.filter((o: OpeningBasic) => {
            const raw = normalize(o?.name);
            const trans = normalize(translateText(o?.name, 'es')); // Always allow searching in Spanish even if UI in English
            const eco = normalize(o?.eco);
            
            const fullTarget = `${raw} ${trans} ${eco}`;
            return terms.every(t => fullTarget.includes(t));
        });

        console.log('--- OpeningsPage: Global Search ---', {
            query: queryNorm,
            found: results.length,
            dbSize: source.length
        });
        
        return results.slice(0, 50);
    }, [search, allOpeningsFlat]);

    useEffect(() => {
        console.log('--- OpeningsPage Rendered v3.5 ---', {
            coreCount: Number(coreOpenings?.length || 0),
            isLoaded: Boolean(isLoaded),
            lang: language
        });
    }, [coreOpenings, isLoaded, language]);

    useEffect(() => {
        if (Boolean(selectedOpening)) {
            setPlayColor('w');
        }
    }, [selectedOpening]);

    useEffect(() => {
        Promise.all([
            api.get('/repertoire').catch(() => ({ data: [] })),
            api.get('/training/progress').catch(() => ({ data: [] }))
        ]).then(([repRes, progRes]) => {
            const rData = Array.isArray(repRes.data) ? repRes.data : [];
            const pData = Array.isArray(progRes.data) ? progRes.data : [];
            setRepertoire(rData);
            setProgress(pData);
            setLoading(false);
            if (Array.isArray(coreOpenings) && coreOpenings.length > 0 && !selectedOpening) {
                setSelectedOpening(coreOpenings[0]);
            }
        }).catch(() => setLoading(false));
    }, [coreOpenings, selectedOpening]);

    // Set showSidebar based on screen size on mount
    useEffect(() => {
        const isMobile = Boolean(window.innerWidth < 1024);
        if (isMobile) setShowSidebar(false);
    }, []);

    const resetSession = () => setSessionConfig(null);

    // --- Safe Derived values BEFORE early returns ---
    const totalCount = Number(allOpeningsFlat?.length || 0);
    const safeRepertoire = Array.isArray(repertoire) ? repertoire : [];
    const repEcos = new Set(safeRepertoire.map((r: any) => String(r.eco || '')));
    const selectedProgressValue = (progress || []).find(p => String(p.eco) === String(selectedOpening?.eco))?.progress || 0;
    const isFav = Boolean(selectedOpening && repEcos.has(String(selectedOpening.eco)));

    if (Boolean(loading)) return (
        <div className="h-full flex flex-col items-center justify-center gap-6 animate-in fade-in duration-700 bg-[#0a0c10]">
            <RefreshCw className="animate-spin text-indigo-400" size={32} />
            <div className="text-[10px] font-black text-white/40 uppercase tracking-[0.5em]">Iniciando Academia</div>
        </div>
    );

    // ── Active session view ──
    if (Boolean(sessionConfig)) {
        const config = sessionConfig!;
        return (
            <div className="flex flex-col h-[100dvh] lg:h-full overflow-hidden bg-[#0a0c10] animate-in fade-in duration-300">
                <header className="flex items-center justify-between px-4 py-3 bg-black/40 border-b border-white/5 shrink-0 z-10 gap-4">
                    <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                        <button
                            onClick={resetSession}
                            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white transition-all shadow-lg"
                        >
                            <ChevronLeft size={16} />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Biblioteca</span>
                        </button>
                        <div className="h-4 w-px bg-white/10" />
                        <div className="flex flex-col min-w-0">
                            <span className="text-[8px] lg:text-[9px] font-black text-white/20 uppercase tracking-widest">Estudiando</span>
                            <span className="text-[10px] lg:text-[11px] font-black text-white uppercase tracking-widest truncate">
                                {String(config.eco)} · {String(config.name)}
                            </span>
                        </div>
                    </div>

                    <div className="flex bg-black/40 rounded-xl border border-white/5 p-1 shrink-0">
                        {(['theory', 'training', 'exercises'] as const).map((m) => (
                            <button
                                key={String(m)}
                                onClick={() => setSessionConfig({ ...config, mode: m })}
                                className={`px-2 lg:px-4 py-2 text-[8px] lg:text-[10px] font-black uppercase tracking-widest transition-all rounded-lg ${config.mode === m ? 'bg-indigo-500 text-white shadow-[0_0_15px_rgba(99,102,241,0.3)]' : 'text-white/30 hover:text-white'}`}
                            >
                                {m === 'theory' && 'Teoría'}
                                {m === 'training' && 'Práctica'}
                                {m === 'exercises' && 'Test'}
                            </button>
                        ))}
                    </div>
                </header>

                <main className="flex-1 overflow-hidden relative">
                    {config.mode === 'theory' && <OpeningsTheory eco={String(config.eco)} color={config.color} onExit={resetSession} />}
                    {config.mode === 'training' && <OpeningTraining eco={String(config.eco)} color={config.color} onExit={resetSession} />}
                    {config.mode === 'exercises' && <OpeningExercises eco={String(config.eco)} color={config.color} />}
                </main>
            </div>
        );
    }

    const currentSidebarList = Boolean(search) ? filteredOpenings : (Array.isArray(coreOpenings) ? coreOpenings : []);

    return (
        <div className="flex flex-col lg:flex-row h-full overflow-hidden bg-[#0a0c10] relative">
            <div className="lg:hidden flex items-center justify-between p-4 bg-black/40 border-b border-white/5 z-30 shrink-0">
                <div className="flex items-center gap-3">
                    <BookOpen size={20} className="text-indigo-400" />
                    <span className="text-xs font-black text-white uppercase tracking-widest">Academia</span>
                </div>
                <button onClick={() => setShowSidebar(!showSidebar)} className="p-2 rounded-xl bg-white/5 text-white">
                    {Boolean(showSidebar) ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <aside className={`
                w-full lg:w-[400px] lg:h-full flex flex-col border-r border-white/5 bg-[#0a0a0a] z-20 shrink-0 overflow-hidden
                fixed lg:relative inset-0 transition-transform duration-500 ease-[cubic-bezier(0.16, 1, 0.3, 1)]
                ${Boolean(showSidebar) ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                pt-16 lg:pt-0
            `}>
                <div className="p-6 border-b border-white/5">
                    <div className="hidden lg:flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
                            <BookOpen size={20} />
                        </div>
                        <h1 className="text-lg font-black text-white uppercase tracking-tighter">Chessdrez <span className="text-indigo-400">Theory</span></h1>
                    </div>

                    <div className="relative group">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            placeholder="BUSCAR APERTURA..."
                            value={String(search)}
                            onChange={e => setSearch(String(e.target.value))}
                            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black text-white placeholder:text-white/20 focus:bg-white/10 focus:border-indigo-400/40 focus:outline-none transition-all uppercase tracking-widest"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                    {Boolean(safeRepertoire.length > 0) && (
                        <AccordionSection
                            title="Favoritas"
                            icon={<Heart size={14} fill="currentColor" className="text-rose-500" />}
                            count={Number(safeRepertoire.length)}
                            isOpen={openSection === 'favorites'}
                            onToggle={() => setOpenSection(openSection === 'favorites' ? null : 'favorites')}
                        >
                                {safeRepertoire.map((r: any, idx: number) => {
                                    const op = r?.opening;
                                    if (!op || typeof op !== 'object') return null;
                                    const opEco = String(op.eco || '');
                                    const isSelected = Boolean(selectedOpening && selectedOpening.eco === opEco);
                                    return (
                                        <button
                                            key={`fav-${opEco}-${idx}`}
                                            onClick={() => { setSelectedOpening(op); if (window.innerWidth < 1024) setShowSidebar(false); }}
                                            className={`flex items-center justify-between p-3 rounded-xl transition-all border ${isSelected ? 'bg-rose-500/10 border-rose-500/30' : 'bg-transparent border-transparent hover:bg-white/5'}`}
                                        >
                                            <div className="flex flex-col text-left min-w-0">
                                                <span className={`text-[9px] font-black uppercase tracking-widest ${isSelected ? 'text-rose-400' : 'text-white/40'}`}>{opEco}</span>
                                                <span className="text-xs font-bold text-white truncate uppercase tracking-tight leading-tight">{translateText(op.name, language)}</span>
                                            </div>
                                            {isSelected && <Play size={12} className="text-rose-400 fill-rose-400 shrink-0 ml-2" />}
                                        </button>
                                    );
                                })}
                        </AccordionSection>
                    )}

                    <AccordionSection
                        title="Explorar Aperturas"
                        icon={<div className="w-2.5 h-2.5 rounded-sm bg-indigo-500/80" />}
                        count={Boolean(search) ? Number(filteredOpenings.length) : Number(coreOpenings.length)}
                        isOpen={openSection === 'explore'}
                        onToggle={() => setOpenSection(openSection === 'explore' ? null : 'explore')}
                    >
                        {currentSidebarList.map((op: OpeningBasic, idx: number) => {
                            if (!op || typeof op !== 'object') return null;
                            const opEco = String(op.eco || '');
                            const isSelected = Boolean(selectedOpening && selectedOpening.eco === opEco);
                            return (
                                <button
                                    key={`exp-${opEco}-${idx}`}
                                    onClick={() => { setSelectedOpening(op); if (window.innerWidth < 1024) setShowSidebar(false); }}
                                    className={`flex items-center justify-between p-3 rounded-xl transition-all border ${isSelected ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-transparent border-transparent hover:bg-white/5'}`}
                                >
                                    <div className="flex flex-col text-left min-w-0">
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${isSelected ? 'text-indigo-400' : 'text-white/40'}`}>{opEco}</span>
                                        <span className="text-xs font-bold text-white truncate uppercase tracking-tight leading-tight">{translateText(op.name, language)}</span>
                                    </div>
                                    {isSelected && <Play size={12} className="text-indigo-400 fill-indigo-400 shrink-0 ml-2" />}
                                </button>
                            );
                        })}
                        {Boolean(search && filteredOpenings.length === 0) && (
                            <div className="text-center py-4 text-xs font-bold text-white/40 uppercase tracking-widest">No se encontraron resultados</div>
                        )}
                        {!isLoaded && !search && (
                             <div className="flex items-center justify-center py-4 gap-2 text-white/20">
                                 <RefreshCw size={12} className="animate-spin" />
                                 <span className="text-[9px] font-black uppercase tracking-widest animate-pulse">Cargando base de datos completa...</span>
                             </div>
                        )}
                    </AccordionSection>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto overflow-x-hidden relative flex flex-col items-center justify-center p-4 lg:p-8 bg-[#0a0c10]">
                {Boolean(selectedOpening) ? (
                    <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16 animate-in zoom-in-95 fade-in duration-500">
                        <div className="w-full lg:flex-[3] flex flex-col items-center gap-4">
                            <div className="w-full aspect-square max-w-[500px] bg-black/40 rounded-3xl border border-white/5 shadow-2xl relative p-2 lg:p-4">
                                <ChessBoard
                                    fen={String(selectedOpening!.fenRoot || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1")}
                                    orientation={playColor === 'w' ? 'white' : 'black'}
                                    draggable={false}
                                />
                                <div className="absolute inset-x-0 bottom-4 lg:bottom-8 flex justify-center z-20 pointer-events-none">
                                    <div className="bg-black/80 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/10 text-[8px] lg:text-[9px] font-black text-white/60 tracking-widest uppercase">Vista Previa</div>
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:flex-[2] flex flex-col gap-6">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="px-3 py-1 bg-indigo-500 text-white rounded-lg text-[9px] lg:text-[10px] font-black tracking-widest uppercase">{String(selectedOpening!.eco)}</span>
                                    {Boolean(isFav) && <Heart size={16} fill="currentColor" className="text-rose-500" />}
                                </div>
                                <h2 className="text-2xl lg:text-4xl font-black text-white uppercase tracking-tighter leading-tight mb-4">{translateText(selectedOpening!.name, language)}</h2>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col p-4 bg-white/3 rounded-2xl border border-white/5">
                                    <span className="text-[8px] lg:text-[9px] font-black text-white/20 uppercase tracking-widest mb-1">Dominio</span>
                                    <span className="text-lg lg:text-xl font-black text-white">{String(selectedProgressValue)}%</span>
                                </div>
                                <div className="flex flex-col p-4 bg-white/3 rounded-2xl border border-white/5">
                                    <span className="text-[8px] lg:text-[9px] font-black text-white/20 uppercase tracking-widest mb-1">Estatus</span>
                                    <span className={`text-[10px] lg:text-[11px] font-black uppercase tracking-widest ${Boolean(isFav) ? 'text-rose-400' : 'text-white/10'}`}>{Boolean(isFav) ? 'FAVORITA' : 'BÁSICA'}</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 mt-2">
                                <div className="flex gap-2 p-1.5 bg-white/3 rounded-[20px] border border-white/5">
                                    <button
                                        onClick={() => setPlayColor('w')}
                                        className={`flex-1 py-2.5 rounded-2xl text-[9px] lg:text-[10px] font-black uppercase tracking-widest transition-all ${playColor === 'w' ? 'bg-white text-black shadow-xl scale-[1.02]' : 'text-white/20 hover:text-white'}`}
                                    >Jugar Blancas</button>
                                    <button
                                        onClick={() => setPlayColor('b')}
                                        className={`flex-1 py-2.5 rounded-2xl text-[9px] lg:text-[10px] font-black uppercase tracking-widest transition-all ${playColor === 'b' ? 'bg-black text-white border border-white/20 shadow-xl scale-[1.02]' : 'text-white/20 hover:text-white'}`}
                                    >Jugar Negras</button>
                                </div>

                                <button
                                    onClick={() => setSessionConfig({ eco: String(selectedOpening!.eco), name: String(selectedOpening!.name), mode: 'theory', color: playColor })}
                                    className="w-full bg-indigo-500 hover:bg-indigo-400 text-white py-4 lg:py-5 rounded-3xl font-black text-xs lg:text-sm uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 shadow-2xl shadow-indigo-500/20 active:scale-95"
                                >
                                    <BookOpen size={18} /> Estudiar Teoría
                                </button>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => setSessionConfig({ eco: String(selectedOpening!.eco), name: String(selectedOpening!.name), mode: 'training', color: playColor })}
                                        className="bg-white/5 hover:bg-white/10 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-white/10 transition-all flex items-center justify-center gap-2"
                                    ><Brain size={16} /> Práctica</button>
                                    <button
                                        onClick={() => setSessionConfig({ eco: String(selectedOpening!.eco), name: String(selectedOpening!.name), mode: 'exercises', color: playColor })}
                                        className="bg-white/5 hover:bg-white/10 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-white/10 transition-all flex items-center justify-center gap-2"
                                    ><Star size={16} /> Test</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-6 opacity-10">
                        <span className="text-white/20 font-black uppercase tracking-[0.5em] text-[10px]">-- EXPLORAR {String(totalCount)}+ APERTURAS --</span>
                        <Search size={100} className="text-white/5" />
                    </div>
                )}
            </main>
        </div>
    );
};

export default OpeningsPage;
